import type { FieldErrors, UseFormRegister } from 'react-hook-form'
import { z } from 'zod'
import type { CausaEmergencial, NaturezaDaSolucao, NovaGmudRequest, Prioridade, TipoGmud } from '../../../types/gmud'

/**
 * Teto de cada campo do plano, igual ao `@Size` de `PlanoRequest`.
 *
 * Exportado porque o contador de caracteres da etapa 2 precisa do mesmo número: dois limites
 * escritos em lugares diferentes divergem no primeiro ajuste, e o contador passaria a dizer
 * "faltam 2.000" enquanto o servidor recusa o texto.
 */
export const LIMITE_DO_PLANO = 20000

const textoDoPlano = z.string().max(LIMITE_DO_PLANO, 'Texto muito longo: no máximo 20.000 caracteres.').optional()

/**
 * Etapa 1 — a classificação da mudança.
 *
 * Exige cinco coisas: tipo, prioridade, natureza da solução, sistema e tipo de manutenção. Módulo,
 * sub-módulo e responsável são opcionais porque nenhuma regra depende deles — entram porque o RDM
 * impresso os imprime em destaque e, sem eles no cadastro, o relatório saía com três campos em
 * branco.
 *
 * A natureza da solução (definitiva ou paliativa) é obrigatória já na abertura, e não só no envio
 * ao comitê, porque não há tela que a edite depois: é a mesma regra de `CriarGmudRequest`.
 *
 * O objeto fica exposto sem o refino para que `etapas.ts` leia `.shape` e saiba quais campos são
 * desta etapa. Escrever essa lista à mão ao lado do esquema é como ela envelhece: alguém
 * acrescenta um campo aqui e o assistente continua avançando sem validá-lo.
 */
export const camposDaClassificacao = z.object({
  tipo: z.enum(['PROJETO', 'CHAMADO'], {
    errorMap: () => ({ message: 'Informe se a mudança vem de um projeto ou de um chamado.' }),
  }),
  prioridade: z.enum(['BAIXA', 'MEDIA', 'ALTA', 'EMERGENCIAL'], {
    errorMap: () => ({ message: 'Informe a prioridade da mudança.' }),
  }),
  causaEmergencial: z.enum(['', 'ATENDER_NEGOCIO', 'PROBLEMA_SOFTWARE', 'ERRO_IMPLEMENTACAO']).default(''),
  justificativaEmergencial: z.string().max(4000, 'Texto muito longo: no máximo 4.000 caracteres.').optional(),
  chamadoNumero: z
    .string()
    .max(40, 'O número do chamado tem no máximo 40 caracteres.')
    .regex(/^[0-9./\- ]*$/, 'O número do chamado aceita apenas dígitos e separadores.')
    .optional(),
  naturezaDaSolucao: z.enum(['DEFINITIVA', 'PALIATIVA'], {
    errorMap: () => ({ message: 'Informe se a mudança é a solução definitiva do problema ou uma solução paliativa.' }),
  }),
  sistemaId: z.coerce.number().int().positive('Escolha o sistema afetado.'),
  finalidadeId: z.coerce.number().int().positive('Escolha o tipo de manutenção.'),
  modulo: z.string().max(120, 'O módulo aceita no máximo 120 caracteres.').optional(),
  subModulo: z.string().max(120, 'O sub-módulo aceita no máximo 120 caracteres.').optional(),
  // O id da conta escolhida, como texto — é o que um `<select>` devolve. Vazio significa "ainda
  // não definido", que é um estado legítimo: a solicitação nasce em rascunho e o responsável pode
  // ser definido depois, na própria solicitação.
  //
  // Era um enum de duas opções ('' e 'EU') enquanto não havia como listar pessoas. Havia doze
  // contas ativas e nenhuma delas podia ser escolhida a não ser a de quem preenchia — de modo que
  // uma mudança executada pela contratada nascia sem responsável, ou com o solicitante no lugar
  // dele.
  responsavel: z.string().default(''),
})

/** Etapa 2 — os oito artefatos do plano. Todos opcionais: a solicitação nasce em rascunho. */
export const camposDoPlano = z.object({
  justificativa: textoDoPlano,
  preRequisitos: textoDoPlano,
  requisitos: textoDoPlano,
  procedimentosImplementacao: textoDoPlano,
  planoRollback: textoDoPlano,
  planoContingencia: textoDoPlano,
  testeVolta: textoDoPlano,
  evidenciaTesteDescricao: textoDoPlano,
})

/**
 * Item 8.31.5.2 do TR: emergencial sem causa declarada não existe.
 *
 * O servidor recusa de qualquer forma; avisar aqui poupa o retrabalho de reabrir a tela — e, num
 * assistente em etapas, poupa descobrir na etapa 3 que faltava um campo da etapa 1.
 */
function exigirCausaDeclarada(
  valores: { prioridade: Prioridade; causaEmergencial: '' | CausaEmergencial },
  contexto: z.RefinementCtx,
) {
  if (valores.prioridade === 'EMERGENCIAL' && !valores.causaEmergencial) {
    contexto.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['causaEmergencial'],
      message: 'Toda mudança emergencial precisa declarar a causa (item 8.31.5.2 do Termo de Referência).',
    })
  }
}

/** O que a etapa 1 valida antes de deixar avançar. */
export const esquemaDaClassificacao = camposDaClassificacao.superRefine(exigirCausaDeclarada)

/** O que a etapa 2 valida. Só tamanho: nada do plano é obrigatório para salvar o rascunho. */
export const esquemaDoPlano = camposDoPlano

/**
 * O formulário inteiro, conferido de uma vez antes de salvar.
 *
 * Estas regras espelham as anotações de `CriarGmudRequest` e existem para o usuário não perder o
 * preenchimento numa ida ao servidor. Quem valida de verdade é o backend. Um formulário que se
 * recusa a salvar até estar perfeito é o que faz as pessoas manterem o plano num documento à parte
 * e colarem tudo no fim, quando já não dá para revisar.
 */
export const esquemaNovaGmud = camposDaClassificacao.merge(camposDoPlano).superRefine(exigirCausaDeclarada)

export type FormularioNovaGmud = z.infer<typeof esquemaNovaGmud>

/**
 * O que todo bloco de campos recebe.
 *
 * Repetida arquivo a arquivo, esta interface é onde as divergências começam — um bloco passa a
 * receber `errors` opcional, para de mostrar erro, e ninguém vê porque a tela não quebra.
 */
export interface PropsDosCampos {
  register: UseFormRegister<FormularioNovaGmud>
  errors: FieldErrors<FormularioNovaGmud>
}

/**
 * Converte o formulário no corpo de `POST /gmuds`.
 *
 * Campo em branco vira ausente, e não string vazia: o backend trata ausente como "não informado" e
 * string vazia como "apague o que estava lá" — a diferença importa quando o mesmo plano for
 * editado depois por PATCH.
 *
 * Os campos da emergência só viajam quando a prioridade é emergencial. Não é zelo: os campos somem
 * da tela ao trocar a prioridade, mas o `react-hook-form` **guarda o que já foi digitado** neles —
 * e no assistente em etapas isso vale para a etapa inteira, que desmonta ao avançar. Sem esta poda,
 * quem escolhe emergencial, preenche a causa e depois volta para "baixa" grava uma causa de
 * emergência numa mudança que não é emergencial. E isso passa em silêncio nas duas pontas: a
 * invariante do domínio (`exigirCausaQuandoEmergencial`) e o CHECK do banco
 * (`ck_emergencial_exige_causa`) são de mão única — cobram causa quando é emergencial, não proíbem
 * causa quando não é. O registro seria aceito e entraria torto no relatório mensal de fiscalização
 * (TR 8.31.5.2), que é justamente o que alimenta essa coluna.
 *
 * @param valores o que o usuário preencheu
 * @param idDoUsuario id de quem está preenchendo, para quando ele se declara responsável
 * @returns o corpo a enviar
 */
export function paraRequisicao(valores: FormularioNovaGmud, idDoUsuario: number | null): NovaGmudRequest {
  const emergencial = valores.prioridade === 'EMERGENCIAL'
  const plano = {
    justificativa: valores.justificativa,
    preRequisitos: valores.preRequisitos,
    requisitos: valores.requisitos,
    procedimentosImplementacao: valores.procedimentosImplementacao,
    planoRollback: valores.planoRollback,
    planoContingencia: valores.planoContingencia,
    testeVolta: valores.testeVolta,
    evidenciaTesteDescricao: valores.evidenciaTesteDescricao,
  }

  const preenchidos = Object.fromEntries(Object.entries(plano).filter(([, texto]) => Boolean(texto?.trim())))

  return {
    tipo: valores.tipo as TipoGmud,
    prioridade: valores.prioridade as Prioridade,
    causaEmergencial:
      emergencial && valores.causaEmergencial ? (valores.causaEmergencial as CausaEmergencial) : null,
    justificativaEmergencial: emergencial ? vazioViraNulo(valores.justificativaEmergencial) : null,
    chamadoNumero: vazioViraNulo(valores.chamadoNumero),
    naturezaDaSolucao: valores.naturezaDaSolucao as NaturezaDaSolucao,
    sistemaId: valores.sistemaId,
    finalidadeId: valores.finalidadeId,
    modulo: vazioViraNulo(valores.modulo),
    subModulo: vazioViraNulo(valores.subModulo),
    // `exigirReferencia('usuario', …)` recusa id inexistente com 404. 'EU' continua aceito para
    // não invalidar rascunho salvo antes da lista existir.
    responsavelId: idDoResponsavel(valores.responsavel, idDoUsuario),
    plano: Object.keys(preenchidos).length > 0 ? preenchidos : undefined,
  }
}

/** O id escolhido no select, ou o de quem preencheu quando o rascunho ainda diz 'EU'. */
function idDoResponsavel(escolhido: string, idDoUsuario: number | null): number | null {
  if (escolhido === 'EU') {
    return idDoUsuario
  }
  const id = Number(escolhido)
  return Number.isInteger(id) && id > 0 ? id : null
}

function vazioViraNulo(texto: string | undefined): string | null {
  const limpo = texto?.trim()
  return limpo ? limpo : null
}
